import { useSignals } from "@preact/signals-react/runtime";
import { lazy, Suspense, type ReactNode } from "react";
import { Redirect, Route, Switch } from "wouter";

import { dataError, dataLoading, dataReady } from "@/app/state/app-state";
import { LoginPage } from "@/pages/authentication";
import { sessionReady, sessionUser } from "@/pages/authentication/state/session";
import { homeRoute } from "@/pages/authentication/utils/auth";
import ExternalAccessGate from "@/pages/external/components/ExternalAccessGate";
import { syncConnectionState, syncRuntime } from "@/shared/api/sync";

const BedsPage = lazy(() => import("@/pages/beds/pages/BedsPage"));
const HAPEntryPage = lazy(() => import("@/pages/hap/pages/HAPEntryPage"));
const HAPPage = lazy(() => import("@/pages/hap/pages/HAPPage"));
const ManagePage = lazy(() => import("@/pages/administration/pages/ManagePage"));
const LogoutPage = lazy(() => import("@/pages/authentication/pages/LogoutPage"));
const PatientsPage = lazy(() => import("@/pages/patients/pages/PatientsPage"));
const PublicStatusReportPage = lazy(() => import("@/pages/reports/pages/PublicStatusReportPage"));
const PublicBedBoardPage = lazy(() => import("@/pages/reports/pages/PublicBedBoardPage"));
const ReportBedBoardPrintPage = lazy(() => import("@/pages/reports/pages/ReportBedBoardPrintPage"));
const ReportPage = lazy(() => import("@/pages/reports/pages/ReportPage"));
const ReportStatusPrintPage = lazy(() => import("@/pages/reports/pages/ReportStatusPrintPage"));
const StatisticsPage = lazy(() => import("@/pages/statistics/pages/StatisticsPage"));
const StatusPage = lazy(() => import("@/pages/status/pages/StatusPage"));
const TelefonistPage = lazy(() => import("@/pages/telefonist/pages/TelefonistPage"));
const ExternalPage = lazy(() => import("@/pages/external/pages/ExternalPage"));
const InboxPage = lazy(() => import("@/pages/inbox/pages/InboxPage"));
const HandoverWorkspacePage = lazy(() => import("@/pages/handover/pages/HandoverWorkspacePage"));

function LoadingScreen({ title, detail }: { title: string; detail: string }) {
    return (
        <div className="grid h-screen w-screen place-items-center bg-slate-100 text-sm font-semibold text-slate-500">
            <div className="flex w-72 flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin [animation-duration:0.2s] rounded-full border-2 border-slate-300 border-t-[#005fb8]" />
                <div className="text-center">
                    <p>{title}</p>
                    <p className="mt-1 text-xs font-normal text-slate-400">{detail}</p>
                </div>
            </div>
        </div>
    );
}

function getLoadingDetail() {
    if (!sessionUser.value) {
        return "Waiting for a signed-in user.";
    }

    if (!dataReady.value && dataLoading.value) {
        return syncConnectionState.value === "connecting"
            ? `Connecting to websocket at ${syncRuntime.value.currentURL ?? "unknown"}...`
            : syncConnectionState.value === "connected"
                ? "Sync connected, loading application data..."
                : "Loading application data...";
    }

    return syncConnectionState.value === "connected"
        ? "Sync is connected and ready."
        : syncConnectionState.value === "connecting"
            ? `Connecting to websocket at ${syncRuntime.value.currentURL ?? "unknown"}...`
            : "Preparing application...";
}

function ProtectedRoute({ children }: { children: ReactNode }) {
    useSignals();

    if (!sessionReady.value) {
        return <LoadingScreen title="Checking session..." detail="Validating authentication and restoring your session." />;
    }

    if (!sessionUser.value) {
        return <Redirect to="/login" />;
    }

    if (homeRoute(sessionUser.value) === "/handover") return <Redirect to="/handover" />;
    if (sessionUser.value.role === "HAP") {
        return <Redirect to="/hap" />;
    }

    if (dataLoading.value && !dataReady.value) {
        return <LoadingScreen title="Loading data..." detail={getLoadingDetail()} />;
    }

    if (dataError.value && !dataReady.value) {
        return <div className="grid h-screen w-screen place-items-center bg-slate-100 px-6 text-center text-sm font-semibold text-rose-600">{dataError.value}</div>;
    }

    return <>{children}</>;
}

function ManageRoute({ children }: { children: ReactNode }) {
    useSignals();

    if (!sessionReady.value) {
        return <LoadingScreen title="Checking session..." detail="Validating authentication and restoring your session." />;
    }

    if (!sessionUser.value) {
        return <Redirect to="/login" />;
    }

    if (homeRoute(sessionUser.value) === "/handover") return <Redirect to="/handover" />;
    if (sessionUser.value.role === "HAP") {
        return <Redirect to="/hap" />;
    }

    return <>{children}</>;
}

function HAPRoute({ children }: { children: ReactNode }) {
    useSignals();

    if (!sessionReady.value) {
        return <LoadingScreen title="Checking session..." detail="Validating authentication and restoring your session." />;
    }

    if (!sessionUser.value) {
        return <Redirect to="/login" />;
    }

    if (homeRoute(sessionUser.value) === "/handover") return <Redirect to="/handover" />;
    if (!["Administrator", "Manager", "HAP"].includes(sessionUser.value.role)) {
        return <Redirect to="/beds" />;
    }

    if (dataLoading.value && !dataReady.value) {
        return <LoadingScreen title="Loading HAP..." detail="Loading HAP schedule." />;
    }

    if (dataError.value && !dataReady.value) {
        return <div className="grid h-screen w-screen place-items-center bg-slate-100 px-6 text-center text-sm font-semibold text-rose-600">{dataError.value}</div>;
    }

    return <>{children}</>;
}

export default function AppRouter() {
    useSignals();
    return (
        <Suspense fallback={<LoadingScreen title="Loading page..." detail="Preparing requested page." />}>
            <Switch>
                <Route path="/">
                    <Redirect to={homeRoute(sessionUser.value)} />
                </Route>

                <Route path="/beds">
                    <ProtectedRoute><BedsPage /></ProtectedRoute>
                </Route>

                <Route path="/events">
                    <ProtectedRoute><Redirect to="/inbox/events" /></ProtectedRoute>
                </Route>

                <Route path="/patients">
                    <ProtectedRoute><PatientsPage /></ProtectedRoute>
                </Route>

                <Route path="/report">
                    <ProtectedRoute><ReportPage /></ProtectedRoute>
                </Route>

                <Route path="/report/print/bed-board">
                    <ProtectedRoute><ReportBedBoardPrintPage /></ProtectedRoute>
                </Route>

                <Route path="/report/print/status-report">
                    <ProtectedRoute><ReportStatusPrintPage /></ProtectedRoute>
                </Route>

                <Route path="/status-report">
                    <ExternalAccessGate><PublicStatusReportPage /></ExternalAccessGate>
                </Route>

                <Route path="/external"><ExternalAccessGate><ExternalPage /></ExternalAccessGate></Route>
                <Route path="/external/status-report"><ExternalAccessGate><PublicStatusReportPage /></ExternalAccessGate></Route>
                <Route path="/external/bed-board"><ExternalAccessGate><PublicBedBoardPage /></ExternalAccessGate></Route>
                <Route path="/external/telefonist"><ExternalAccessGate><TelefonistPage /></ExternalAccessGate></Route>
                <Route path="/external/hap-entry"><ExternalAccessGate><HAPEntryPage /></ExternalAccessGate></Route>
                <Route path="/external/handover"><ExternalHandoverEntryRoute /></Route>

                <Route path="/bed-board">
                    <ExternalAccessGate><PublicBedBoardPage /></ExternalAccessGate>
                </Route>

                <Route path="/telefonist">
                    <ExternalAccessGate><TelefonistPage /></ExternalAccessGate>
                </Route>

                <Route path="/hap/entry">
                    <ExternalAccessGate><HAPEntryPage /></ExternalAccessGate>
                </Route>

                <Route path="/hap">
                    <HAPRoute><HAPPage /></HAPRoute>
                </Route>

                <Route path="/handover">
                    <HandoverRoute />
                </Route>

                <Route path="/inbox/*?">
                    <ProtectedRoute><InboxPage /></ProtectedRoute>
                </Route>

                <Route path="/statistics">
                    <ProtectedRoute><StatisticsPage /></ProtectedRoute>
                </Route>

                <Route path="/manage/*?">
                    <ManageRoute><ManagePage /></ManageRoute>
                </Route>

                <Route path="/status">
                    <ProtectedRoute><StatusPage /></ProtectedRoute>
                </Route>

                <Route path="/status/*">
                    <ProtectedRoute><StatusPage /></ProtectedRoute>
                </Route>

                <Route path="/login">
                    <LoginPage />
                </Route>

                <Route path="/logout">
                    <LogoutPage />
                </Route>

                <Route>
                    <Redirect to={homeRoute(sessionUser.value)} />
                </Route>
            </Switch>
        </Suspense>
    );
}

function HandoverRoute() {
    useSignals();
    if (!sessionReady.value) return <LoadingScreen title="Checking session..." detail="Loading handover access." />;
    if (!sessionUser.value) return <Redirect to="/login" />;
    if (homeRoute(sessionUser.value) === "/handover") return <HandoverWorkspacePage />;
    if (sessionUser.value.role === "HAP") return <Redirect to="/hap" />;
    return <Redirect to="/inbox/handovers" />;
}

function ExternalHandoverEntryRoute() {
    useSignals();
    if (!sessionReady.value) return <LoadingScreen title="Checking session..." detail="Handover requires an assigned user account." />;
    if (!sessionUser.value) return <Redirect to="/login" />;
    return <Redirect to={homeRoute(sessionUser.value)} />;
}
